import cloneDeep from 'clone-deep'
import { SettingKey } from 'config'
import { getShareFeaturePages, NEXYS_SHARE_FEATURE_OPTIONS } from 'config/nexysShareFeatures'
import { accountTypes } from 'config/user.type.constants'
import type { ILoanGlobalSettings } from 'pages/Admin/AdminTools/Configuration/GlobalSettings/interface'
import { useEffect, useMemo, useState } from 'react'
import { getLoanTypeMapForSelect, LoanType } from 'reducers/loanDetail.reducer'
import { getAdminConfig, getAdminEmails, getTemplates, setAdminConfig } from 'services'
import { svgLoading } from 'stories/assets'
import { ButtonGroup, MultiSelect, ToggleButton } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { NexysPeerPagePermissionsModal } from './NexysPeerPagePermissionsModal'

interface IProjectInfo {
  name: string
  title: string
  logo: string
}

type NexysSettingsSlice = Pick<
  ILoanGlobalSettings,
  | 'nexysConnectionAdmin'
  | 'nexysConnections'
  | 'nexysSharedTemplates'
  | 'nexysSharedDisplay'
  | 'nexysPullChannel'
  | 'defaultChannel'
>

export const NexysConnections = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [globalSetting, setGlobalSetting] = useState<ILoanGlobalSettings | null>(null)
  const [adminEmailsList, setAdminEmailsList] = useState<Record<string, string>>({})
  const [nexysConnectionAdmin, setNexysConnectionAdmin] = useState<Record<string, boolean>>({})
  const [nexysConnections, setNexysConnections] = useState<Record<string, boolean>>({})
  const [nexysSharedTemplates, setNexysSharedTemplates] = useState<Record<string, Record<string, boolean>>>({})
  const [nexysSharedDisplay, setNexysSharedDisplay] = useState<ILoanGlobalSettings['nexysSharedDisplay']>({})
  const [nexysPullChannel, setNexysPullChannel] = useState<Record<string, LoanType>>({})
  const [projectsMap, setProjectsMap] = useState<IProjectInfo[]>([])
  const [featuresConfig, setFeaturesConfig] = useState<Record<string, any>>({})
  const [templateOptions, setTemplateOptions] = useState<Record<string, string>>({})
  const [selectedPeer, setSelectedPeer] = useState('')
  const [permissionModal, setPermissionModal] = useState<{ projectId: string; featureKey: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    Promise.all([
      getAdminConfig(SettingKey.GLOBAL_SETTINGS),
      getAdminEmails(),
      getAdminConfig('projectsMap'),
      getAdminConfig('features'),
      getTemplates(),
    ])
      .then(([settings, emails, projects, features, templatesRes]: any[]) => {
        if (cancelled) return
        setGlobalSetting(settings)
        setNexysConnectionAdmin(settings?.nexysConnectionAdmin || {})
        setNexysConnections(settings?.nexysConnections || {})
        setNexysSharedTemplates(settings?.nexysSharedTemplates || {})
        setNexysSharedDisplay(settings?.nexysSharedDisplay || {})
        setNexysPullChannel(settings?.nexysPullChannel || {})

        const newOptions: Record<string, string> = {}
        Object.keys(accountTypes).forEach((key) => (newOptions[key] = accountTypes[key]))
        ;(emails as string[]).forEach((email) => (newOptions[email] = email))
        setAdminEmailsList(newOptions)

        setProjectsMap(projects || [])
        setFeaturesConfig(features || {})

        const templates: { no: number; name: string }[] = templatesRes?.data || templatesRes || []
        const options: Record<string, string> = {}
        templates.forEach((t) => {
          options[String(t.no)] = `${t.no} - ${t.name}`
        })
        setTemplateOptions(options)

        const peerIds = Object.keys(settings?.nexysConnections || {})
        if (peerIds.length) setSelectedPeer(peerIds[0])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const peerMenus = useMemo(() => {
    const menus: Record<string, string> = {}
    Object.keys(nexysConnections).forEach((projectId) => {
      const project = projectsMap.find((p) => p.name === projectId)
      const connected = !!nexysConnections[projectId]
      menus[projectId] = `${project?.title || projectId}${connected ? ' (Connected)' : ''}`
    })
    return menus
  }, [nexysConnections, projectsMap])

  const channelOptions = useMemo(() => getLoanTypeMapForSelect(), [])

  const saveSettings = async (updates: Partial<NexysSettingsSlice>) => {
    if (!globalSetting) return
    setIsSaving(true)
    const newSettings = cloneDeep(globalSetting) as ILoanGlobalSettings
    Object.assign(newSettings, updates)
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSettings)
    setGlobalSetting(newSettings)
    setIsSaving(false)
  }

  const onChangeNexysConnectionAdmin = (value: Record<string, boolean>) => {
    setNexysConnectionAdmin(value)
  }

  const onBlurNexysConnectionAdmin = async () => {
    await saveSettings({ nexysConnectionAdmin })
  }

  const onChangeNexysConnections = async (key: string, value: boolean) => {
    const newConnections = cloneDeep(nexysConnections)
    newConnections[key] = value
    setNexysConnections(newConnections)
    await saveSettings({ nexysConnections: newConnections })
  }

  const onChangeSharedTemplates = (projectId: string, value: Record<string, boolean>) => {
    const next = cloneDeep(nexysSharedTemplates)
    next[projectId] = value
    setNexysSharedTemplates(next)
  }

  const onBlurSharedTemplates = async () => {
    await saveSettings({ nexysSharedTemplates })
  }

  const onChangeSharedFeatureEnabled = async (projectId: string, featureKey: string, enabled: boolean) => {
    const next = cloneDeep(nexysSharedDisplay || {})
    next[projectId] = next[projectId] || {}
    next[projectId][featureKey] = {
      ...(next[projectId][featureKey] || {}),
      enabled,
    }
    setNexysSharedDisplay(next)
    await saveSettings({ nexysSharedDisplay: next })
  }

  const onChangePullChannel = async (projectId: string, value: string) => {
    const channel = Number(value) as LoanType
    const next = cloneDeep(nexysPullChannel || {})
    next[projectId] = channel
    setNexysPullChannel(next)
    await saveSettings({ nexysPullChannel: next })
  }

  const onSavePagePermissions = async (
    projectId: string,
    featureKey: string,
    pagePermissions: Record<string, boolean>,
  ) => {
    const next = cloneDeep(nexysSharedDisplay || {})
    next[projectId] = next[projectId] || {}
    next[projectId][featureKey] = {
      ...(next[projectId][featureKey] || {}),
      pagePermissions,
    }
    setNexysSharedDisplay(next)
    setPermissionModal(null)
    await saveSettings({ nexysSharedDisplay: next })
  }

  const peerIds = Object.keys(nexysConnections)
  const activePeer = selectedPeer && nexysConnections[selectedPeer] !== undefined ? selectedPeer : peerIds[0] || ''
  const project = projectsMap.find((p) => p.name === activePeer)
  const connected = !!nexysConnections[activePeer]
  const peerDisplay = nexysSharedDisplay?.[activePeer] || {}
  const defaultChannelFallback = globalSetting?.defaultChannel ?? LoanType.DEFAULT
  const pullChannelValue =
    nexysPullChannel[activePeer] !== undefined && nexysPullChannel[activePeer] !== null
      ? String(nexysPullChannel[activePeer])
      : String(defaultChannelFallback)

  if (isLoading) {
    return (
      <div className="mt-8 flex items-center gap-2 text-sm text-gray-600">
        <img src={svgLoading} className="inline w-5 h-5 animate-spin" alt="" />
        Loading connections…
      </div>
    )
  }

  return (
    <div className="relative mt-8 border-t border-gray-200 pt-6">
      <div className="mb-4 flex items-center gap-2">
        <RenderInput input={{ inputType: 'section', title: 'Pull' }} Key="nexysPullSection" onChange={() => {}} />
        {isSaving && <img src={svgLoading} className="inline w-5 h-5 animate-spin" alt="" />}
      </div>

      <div className="input md:max-w-2xl mb-4">
        <MultiSelect
          id="nexysConnectionAdmin"
          title="Nexys to Nexys Connection Admin"
          key="nexysConnectionAdmin"
          options={adminEmailsList}
          value={nexysConnectionAdmin}
          defaultSelected={false}
          onChange={onChangeNexysConnectionAdmin}
          onBlur={onBlurNexysConnectionAdmin}
        />
      </div>

      {!peerIds.length ? (
        <p className="text-sm text-gray-500">No peer companies are linked yet.</p>
      ) : (
        <>
          <div className="py-3">
            <ButtonGroup title={peerMenus} value={activePeer} onChange={(value) => setSelectedPeer(value)} />
          </div>

          {activePeer && (
            <div className="flex flex-col gap-3 border border-gray-200 rounded p-4 md:max-w-2xl">
              <ToggleButton
                id={`nexysConnection_${activePeer}`}
                title={`Accept loans from ${project?.title || activePeer}`}
                value={connected}
                onChange={(value) => onChangeNexysConnections(activePeer, value)}
                onBlur={() => {}}
              />

              {connected && (
                <>
                  <div className="input">
                    <RenderInput
                      input={{
                        inputType: 'select',
                        title: 'Default Channel for inbound loans',
                        options: channelOptions,
                        value: pullChannelValue,
                        hasDefaultOption: false,
                        required: true,
                      }}
                      Key={`nexysPullChannel_${activePeer}`}
                      onChange={(_: string, value: string) => onChangePullChannel(activePeer, value)}
                    />
                  </div>

                  <MultiSelect
                    id={`nexysSharedTemplates_${activePeer}`}
                    title={`Share UW Condition Templates with ${project?.title || activePeer}`}
                    options={templateOptions}
                    value={nexysSharedTemplates[activePeer] || {}}
                    defaultSelected={false}
                    onChange={(value) => onChangeSharedTemplates(activePeer, value)}
                    onBlur={onBlurSharedTemplates}
                  />

                  {Object.entries(NEXYS_SHARE_FEATURE_OPTIONS).map(([featureKey, featureTitle]) => (
                    <div key={featureKey} className="border-t border-dotted pt-2 mt-1 space-y-2">
                      <ToggleButton
                        id={`nexysSharedDisplay_${activePeer}_${featureKey}`}
                        title={`Share ${featureTitle}`}
                        value={peerDisplay[featureKey]?.enabled !== false}
                        onChange={(value) => onChangeSharedFeatureEnabled(activePeer, featureKey, value)}
                        onBlur={() => {}}
                      />
                      <button
                        type="button"
                        className="text-sm text-shade-blue hover:underline"
                        onClick={() => setPermissionModal({ projectId: activePeer, featureKey })}
                      >
                        Page Permissions for {featureTitle}
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}

      {permissionModal && (
        <NexysPeerPagePermissionsModal
          featureKey={permissionModal.featureKey}
          featureTitle={NEXYS_SHARE_FEATURE_OPTIONS[permissionModal.featureKey]}
          peerTitle={
            projectsMap.find((p) => p.name === permissionModal.projectId)?.title || permissionModal.projectId
          }
          pages={getShareFeaturePages(permissionModal.featureKey, featuresConfig)}
          pagePermissions={
            nexysSharedDisplay?.[permissionModal.projectId]?.[permissionModal.featureKey]?.pagePermissions || {}
          }
          onClose={() => setPermissionModal(null)}
          onSave={(pagePermissions) =>
            onSavePagePermissions(permissionModal.projectId, permissionModal.featureKey, pagePermissions)
          }
        />
      )}
    </div>
  )
}
